import React from 'react';

/**
 * Degrade-gracefully wrapper for the SAT test's new presentational tools
 * (the figure viewer). If a child throws during render, we show an
 * optional `fallback` (e.g. the plain <img>) instead of letting the error bubble
 * up and white-screen the whole test player — which would block a student from
 * answering/submitting. Purely a safety net; it holds no test/answer state.
 */
type Props = { children: React.ReactNode; fallback?: React.ReactNode };
type State = { hasError: boolean };

export default class ToolErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidCatch(error: unknown) {
        // Swallow: the tool is non-essential; the test must keep working.
        // eslint-disable-next-line no-console
        console.error('SAT tool error (contained by ToolErrorBoundary):', error);
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback ?? null;
        }
        return this.props.children;
    }
}
